import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getComplianceOverview } from "api/brokerApi";
import { unwrapObject } from "api/unwrap";

/**
 * /compliance/overview — tenant-wide audit-readiness rollup. One screen the
 * operator (or their CEO) checks to know whether they'd survive a broker
 * audit today.
 */

const Kpi = ({ label, value, hint, accent }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h3" sx={{ fontWeight: 700, color: accent || "#0f172a", mt: 1 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{hint}</Typography>
      ) : null}
    </CardContent>
  </Card>
);

export const ComplianceOverviewPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getComplianceOverview();
      setData(unwrapObject(res));
    } catch (e) {
      setError(e?.message || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pctAccent =
    !data ? "#0f172a" :
    data.overallPercent >= 90 ? "#16a34a" :
    data.overallPercent >= 70 ? "#f97316" : "#dc2626";

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => navigate("/compliance")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
              Audit Readiness
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Fleet-wide compliance posture across every broker. If a broker calls for an audit today, this is what you'd be defending.
            </Typography>
          </Box>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !data ? null : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi label="Fleet compliance" value={`${data.overallPercent}%`}
                hint={`${data.fullyCompliantDrivers} of ${data.totalDrivers} drivers fully compliant`}
                accent={pctAccent} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi label="Brokers" value={data.totalBrokers}
                hint={`${data.brokersOnAutoEmail} on auto-email`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi label="Packets sent (30d)" value={data.packetsSent30d}
                hint="Manual + auto-emailed audit downloads" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi label="Drivers needing attention" value={(data.topNonCompliantDrivers || []).length}
                hint="Top 10 below"
                accent={(data.topNonCompliantDrivers || []).length > 0 ? "#dc2626" : "#16a34a"} />
            </Grid>
          </Grid>

          {(data.worstBrokers || []).length > 0 ? (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Brokers needing attention
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Broker</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Drivers</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Compliant</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>%</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.worstBrokers.map((b) => (
                        <TableRow key={b.brokerId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.brokerName}</Typography>
                            <Typography variant="caption" color="text.secondary">{b.brokerCode}</Typography>
                          </TableCell>
                          <TableCell align="right">{b.totalDrivers}</TableCell>
                          <TableCell align="right">{b.compliantDrivers}</TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${b.percent}%`}
                              sx={{
                                fontWeight: 700,
                                backgroundColor:
                                  b.percent >= 90 ? "#16a34a" :
                                  b.percent >= 70 ? "#f97316" : "#dc2626",
                                color: "#fff",
                                height: 22,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Button size="small" onClick={() => navigate(`/brokers/${b.brokerId}/compliance`)}>
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ) : null}

          {(data.topNonCompliantDrivers || []).length > 0 ? (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Top non-compliant drivers
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Driver</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Failing brokers</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Missing items</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.topNonCompliantDrivers.map((d) => (
                        <TableRow key={d.driverDetailsId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {d.fullName || "(unnamed)"}
                            </Typography>
                            {d.email ? (
                              <Typography variant="caption" color="text.secondary">{d.email}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {d.failingBrokerCodes || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={d.missingItemsCount}
                              sx={{ backgroundColor: "#dc2626", color: "#fff", fontWeight: 700, height: 22 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </Stack>
  );
};

export default ComplianceOverviewPage;
